import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const CcpEntitlementDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"CcpEntitlement"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"id"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"ccp"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"entitlement"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"id"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"entitlementId"}},{"kind":"Field","name":{"kind":"Name","value":"experienceCapabilities"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"changeOfferingV2"},"directives":[{"kind":"Directive","name":{"kind":"Name","value":"optIn"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"to"},"value":{"kind":"StringValue","value":"CcpAllUserUpgradeAndPay","block":false}}]}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"experienceUrl"}},{"kind":"Field","name":{"kind":"Name","value":"isAvailableToUser"}}]}},{"kind":"Field","name":{"kind":"Name","value":"manageEntitlement"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"experienceUrl"}},{"kind":"Field","name":{"kind":"Name","value":"isAvailableToUser"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"transactionAccountId"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"CcpEntitlement","document":CcpEntitlementDocument}} as const;
export type CcpEntitlementQueryVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
}>;


export type CcpEntitlementQuery = (
  { __typename: 'Query' }
  & { ccp?: Types.Maybe<(
    { __typename: 'CcpQueryApi' }
    & { entitlement?: Types.Maybe<(
      { __typename: 'CcpEntitlement' }
      & Pick<Types.CcpEntitlement, 'id' | 'entitlementId' | 'transactionAccountId'>
      & { experienceCapabilities?: Types.Maybe<(
        { __typename: 'CcpEntitlementExperienceCapabilities' }
        & { changeOfferingV2?: Types.Maybe<(
          { __typename: 'CcpChangeOfferingExperienceCapability' }
          & Pick<Types.CcpChangeOfferingExperienceCapability, 'experienceUrl' | 'isAvailableToUser'>
        )>, manageEntitlement?: Types.Maybe<(
          { __typename: 'CcpManageEntitlementExperienceCapability' }
          & Pick<Types.CcpManageEntitlementExperienceCapability, 'experienceUrl' | 'isAvailableToUser'>
        )> }
      )> }
    )> }
  )> }
);

/**
 * __useCcpEntitlementQuery__
 *
 * To run a query within a React component, call `useCcpEntitlementQuery` and pass it any options that fit your needs.
 * When your component renders, `useCcpEntitlementQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCcpEntitlementQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useCcpEntitlementQuery(
  baseOptions: TrelloQueryHookOptions<
    CcpEntitlementQuery,
    CcpEntitlementQueryVariables
  > &
    (
      | { variables: CcpEntitlementQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: CcpEntitlementDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    CcpEntitlementQuery,
    CcpEntitlementQueryVariables
  >(CcpEntitlementDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useCcpEntitlementLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    CcpEntitlementQuery,
    CcpEntitlementQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<CcpEntitlementQuery, CcpEntitlementQueryVariables>(
    CcpEntitlementDocument,
    options,
  );
}
export function useCcpEntitlementSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        CcpEntitlementQuery,
        CcpEntitlementQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    CcpEntitlementQuery,
    CcpEntitlementQueryVariables
  >(CcpEntitlementDocument, options);
}
export type CcpEntitlementQueryHookResult = ReturnType<
  typeof useCcpEntitlementQuery
>;
export type CcpEntitlementLazyQueryHookResult = ReturnType<
  typeof useCcpEntitlementLazyQuery
>;
export type CcpEntitlementSuspenseQueryHookResult = ReturnType<
  typeof useCcpEntitlementSuspenseQuery
>;
export type CcpEntitlementQueryResult = Apollo.QueryResult<
  CcpEntitlementQuery,
  CcpEntitlementQueryVariables
>;
